import React from 'react';
import styled from 'styled-components';

import { ChevronIconRight } from 'shared/assets/chevron-icon-right';
import { isNotNullish } from 'shared/utils/type-utils';
import { MobileAppFlair, MobileAppProductCollectionDisplayType } from 'types/graphql';
import { ProductCard } from 'src/dispensary-admin/components/product-card';
import { Products } from './product-carousel.types';
import { Container, Header, Title, ViewAllLink, CardsContainer, GridCardsContainer } from './product-carousel.styles';
import { ProductCarouselLoadingSkeleton } from './product-carousel-card/product-carousel-loading-skeleton';
import { ProductGridCard } from './product-grid-card';
import { ProductHeroCarousel } from './product-hero-carousel';

const GRID_PRODUCT_COUNT = 4;

type ProductCarouselProps = {
  label: string;
  loading: boolean;
  products: Products | undefined;
  hasDividerFlair?: boolean;
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
  mobileAppDisplayType?: MobileAppProductCollectionDisplayType | null;
};

export const TEST_ID_CAROUSEL = 'product-carousel';

/** Renders product section content for the selected mobile app display type. */
function renderProductDisplay({
  isGridDisplay,
  isHeroCarouselDisplay,
  displayProducts,
  asMobileApp,
  buttonFlair,
}: {
  isGridDisplay: boolean;
  isHeroCarouselDisplay: boolean;
  displayProducts: NonNullable<Products[number]>[];
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
}): JSX.Element {
  if (isGridDisplay) {
    return (
      <GridCardsContainer data-testid='product-grid'>
        {displayProducts.map((product) => (
          <ProductGridCard key={product.id} product={product} />
        ))}
      </GridCardsContainer>
    );
  }

  if (isHeroCarouselDisplay) {
    return <ProductHeroCarousel products={displayProducts} asMobileApp={asMobileApp} buttonFlair={buttonFlair} />;
  }

  return (
    <CardsContainer $isProductCardsV2Enabled>
      {displayProducts.map((product) => (
        <ProductCardWrapper key={product.id}>
          <ProductCard product={product} asMobileApp={asMobileApp} buttonFlair={buttonFlair} />
        </ProductCardWrapper>
      ))}
    </CardsContainer>
  );
}

export function ProductCarouselV2({
  label,
  products,
  loading,
  hasDividerFlair,
  asMobileApp,
  buttonFlair,
  mobileAppDisplayType,
}: ProductCarouselProps): JSX.Element | null {
  if (!products || loading) {
    return <ProductCarouselLoadingSkeleton />;
  }

  if (!products.length) {
    return null;
  }

  const isGridDisplay = !!asMobileApp && mobileAppDisplayType === MobileAppProductCollectionDisplayType.grid;
  const isHeroCarouselDisplay =
    !!asMobileApp && mobileAppDisplayType === MobileAppProductCollectionDisplayType.heroCarousel;
  const visibleProducts = products.filter(isNotNullish);
  const displayProducts = isGridDisplay ? visibleProducts.slice(0, GRID_PRODUCT_COUNT) : visibleProducts;
  const isCompactDisplay = isGridDisplay || isHeroCarouselDisplay;

  return (
    <Container
      data-testid={TEST_ID_CAROUSEL}
      $asMobileApp={asMobileApp}
      $hasDividerFlair={hasDividerFlair ?? false}
      $isCompact={isCompactDisplay}
    >
      <Header>
        <Title $hasDividerFlair={hasDividerFlair ?? false}>
          {label}
          {hasDividerFlair && <ChevronIconRight fill='#fff' />}
        </Title>
        {!hasDividerFlair && <ViewAllLink>View all</ViewAllLink>}
      </Header>

      {renderProductDisplay({
        isGridDisplay,
        isHeroCarouselDisplay,
        displayProducts,
        asMobileApp,
        buttonFlair,
      })}
    </Container>
  );
}

const ProductCardWrapper = styled.div`
  width: 184px;
`;
